import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";

import AppShell from "../components/AppShell";
import { CardSkeleton } from "../components/Skeleton";
import { getServerUpdate, uploadServerUpdate, type ServerUpdateInfo } from "../api/serverUpdate";

// While an update is on its way: uploading, then waiting for the server PC to pick it up (within a
// minute), then installing (the dashboard goes away for a few minutes and comes back by itself).
type Phase = "idle" | "uploading" | "working";

export default function ServerUpdatePage() {
  const [info, setInfo] = useState<ServerUpdateInfo | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [unreachable, setUnreachable] = useState(false);
  const pageVersion = useRef<string | null>(null);
  const lastAtBefore = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const d = await getServerUpdate();
      setInfo(d);
      setUnreachable(false);
      setLoadError(false);
      pageVersion.current ??= d.installed?.version ?? null;
      return d;
    } catch {
      setUnreachable(true);
      setLoadError(true);
      return null;
    }
  }, []);

  useEffect(() => {
    void refresh().then((d) => {
      // Opened while an update is already on its way.
      if (d && (d.waiting || d.last?.state === "installing")) setPhase("working");
    });
  }, [refresh]);

  // Follow the update until the server reports how it went.
  useEffect(() => {
    if (phase !== "working") return;
    const timer = window.setInterval(() => {
      void refresh().then((d) => {
        if (!d || d.waiting || d.last?.state === "installing") return;
        if (d.last && d.last.at !== lastAtBefore.current) setPhase("idle");
      });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [phase, refresh]);

  async function install() {
    if (!file) return;
    setError(null);
    setPercent(0);
    lastAtBefore.current = info?.last?.at ?? null;
    setPhase("uploading");
    try {
      await uploadServerUpdate(file, setPercent);
      setFile(null);
      setPhase("working");
      void refresh();
    } catch (e) {
      setPhase("idle");
      const detail = isAxiosError(e) ? (e.response?.data as { detail?: string } | undefined)?.detail : undefined;
      setError(detail ?? "The update file couldn't be sent to the server. Check the connection and try again.");
    }
  }

  const last = info?.last ?? null;
  const updated = Boolean(
    last?.state === "done" && pageVersion.current && info?.installed?.version && info.installed.version !== pageVersion.current,
  );

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <h1 className="text-2xl font-semibold text-slate-900">Update KraetdNet</h1>
        <p className="mt-1 text-slate-600">
          Install a new version of KraetdNet on the office server from here. The office computers then update
          themselves, so nobody has to go from PC to PC.
        </p>

        {!info && !loadError ? <CardSkeleton /> : null}
        {!info && loadError ? (
          <p className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            Couldn&rsquo;t reach the office server. Please try again.
          </p>
        ) : null}

        {info && !info.available ? (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            This office server can&rsquo;t be updated from the dashboard yet. Run <b>Install-Server.bat</b> from the
            newest KraetdNet-Server folder on the server PC once; after that, updates are installed from here.
          </div>
        ) : null}

        {info?.available ? (
          <>
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
              Installed version: <span className="font-medium text-slate-900">{info.installed?.version || "unknown"}</span>
            </div>

            {phase === "working" ? (
              <div className="mt-4 rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
                <p className="font-medium">Updating&hellip;</p>
                <p className="mt-1">
                  {info.waiting
                    ? "The server computer starts the update within a minute."
                    : unreachable
                      ? "The server is restarting with the new version. This page reconnects by itself."
                      : last?.state === "installing"
                        ? last.message
                        : "Starting the update."}
                </p>
                <p className="mt-2 text-sky-800">
                  It takes a few minutes. Leave the server computer on. You can close this page.
                </p>
              </div>
            ) : null}

            {phase !== "working" && last?.state === "done" ? (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                <p>{last.message}</p>
                {updated ? (
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="no-tap-highlight mt-3 rounded-lg bg-emerald-700 px-4 py-2 font-medium text-white hover:bg-emerald-800"
                  >
                    Reload the dashboard
                  </button>
                ) : null}
              </div>
            ) : null}
            {phase !== "working" && last?.state === "failed" ? (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <p className="font-medium">The last update didn&rsquo;t install.</p>
                <p className="mt-1">{last.message}</p>
              </div>
            ) : null}

            {info.can_update ? (
              <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                <p className="font-medium text-slate-900">Install an update</p>
                <ol className="mt-2 list-decimal space-y-1 pl-5">
                  <li>
                    Get the update file from KraetdTech. Its name looks like{" "}
                    <span className="font-mono">KraetdNet-Update-0.14.7.kdu</span>.
                  </li>
                  <li>Choose it below and click Install update.</li>
                </ol>
                <p className="mt-2 text-slate-600">
                  Only files made by KraetdTech are accepted. The office data is backed up first, and if anything goes
                  wrong the current version is put back.
                </p>
                <label htmlFor="update-file" className="mt-4 block font-medium text-slate-900">
                  Update file
                </label>
                <input
                  id="update-file"
                  type="file"
                  accept=".kdu"
                  disabled={phase !== "idle"}
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    setError(null);
                  }}
                  className="no-tap-highlight mt-1 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-800 hover:file:bg-slate-200"
                />
                {error ? <p className="mt-3 text-red-700">{error}</p> : null}
                <button
                  type="button"
                  onClick={() => void install()}
                  disabled={!file || phase !== "idle"}
                  className="no-tap-highlight mt-4 rounded-lg bg-slate-900 px-4 py-2 font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                >
                  {phase === "uploading" ? `Sending the file to the server… ${percent}%` : "Install update"}
                </button>
              </div>
            ) : (
              <p className="mt-6 text-sm text-slate-600">Only the office owner can install updates.</p>
            )}
          </>
        ) : null}
      </div>
    </AppShell>
  );
}
